import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LogoMark } from "../components/Logo";
import { toast } from "sonner";
import { api } from "../lib/api";
import { ChevronLeft, CheckCircle2, User, Building2, KeyRound } from "lucide-react";

export default function RegisterEmployee() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [form, setForm] = useState({
    name: "", lastname: "", email: "", password: "", password2: "",
    fiscal_code: "", phone: "",
    company_name: "", vat_number: "",
  });
  const [consents, setConsents] = useState({
    privacy: false, tos: false, share_data: false,
  });
  const up = (k) => (e) => {
    const v = k === "fiscal_code" || k === "vat_number" ? e.target.value.toUpperCase().replace(/\s+/g, "") : e.target.value;
    setForm({ ...form, [k]: v });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.lastname) return toast.error("Nome e cognome obbligatori");
    if (!form.email.includes("@")) return toast.error("Email non valida");
    if (form.password.length < 6) return toast.error("Password minimo 6 caratteri");
    if (form.password !== form.password2) return toast.error("Le password non coincidono");
    if (form.fiscal_code.length !== 16) return toast.error("Codice fiscale non valido (16 caratteri)");
    if (!form.company_name) return toast.error("Indica il nome dell'azienda");
    if (form.vat_number.length < 5) return toast.error("Partita IVA non valida");
    if (!consents.privacy || !consents.tos || !consents.share_data) {
      return toast.error("Devi accettare Privacy Policy, Termini di Servizio e Condivisione dati");
    }
    setLoading(true);
    try {
      const r = await api.post("/auth/register-employee", {
        ...form,
        consent_privacy: consents.privacy,
        consent_tos: consents.tos,
        consent_share_data: consents.share_data,
        privacy_version: "1.0",
        tos_version: "1.0",
      });
      setDone(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore registrazione");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center p-6">
        <div className="bg-white border border-black rounded-3xl p-10 max-w-lg text-center" data-testid="emp-register-success">
          <CheckCircle2 className="w-14 h-14 text-[#3B82F6] mx-auto mb-4"/>
          <h1 className="font-display text-3xl mb-2">Richiesta inviata!</h1>
          <p className="text-[#555] mb-4">{done.message}</p>
          {done.matched_company ? (
            <div className="bg-[#1E2347] border border-[#3B82F6]/30 rounded-xl p-4 text-sm text-[#444] mb-6">
              L'azienda riceverà una notifica e potrà approvare il tuo accesso. Riceverai un'email quando potrai entrare nel portale.
            </div>
          ) : (
            <div className="bg-yellow-50 border border-yellow-300 rounded-xl p-4 text-sm text-yellow-900 mb-6">
              ⚠️ Non abbiamo trovato un'azienda con questa P.IVA tra quelle registrate. Verifica con il tuo datore di lavoro o contatta il super admin.
            </div>
          )}
          <Button onClick={() => nav("/login")} className="rounded-full bg-[#3B82F6] hover:bg-black h-11">
            Torna al login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0E27] py-8 px-4">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <Link to="/" className="flex items-center gap-2 text-[#3B82F6] hover:text-black transition-colors text-sm">
            <ChevronLeft className="w-4 h-4"/> Home
          </Link>
          <LogoMark className="w-9 h-9"/>
        </div>

        <div className="bg-white border border-black rounded-3xl p-8">
          <div className="text-xs uppercase tracking-widest text-[#757575] mb-2">Registrazione dipendente</div>
          <h1 className="font-display text-3xl mb-1">Iscriviti come dipendente</h1>
          <p className="text-sm text-[#555] mb-6">Indica nome, P.IVA della tua azienda e le tue credenziali. L'azienda dovrà approvare il tuo accesso.</p>

          <form onSubmit={submit} className="space-y-5" data-testid="register-employee-form">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#3B82F6] font-bold mb-3 flex items-center gap-2"><User className="w-3.5 h-3.5"/> Tue informazioni</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Nome *</Label>
                  <Input data-testid="emp-reg-name" required className="rounded-xl mt-1" value={form.name} onChange={up("name")}/>
                </div>
                <div>
                  <Label>Cognome *</Label>
                  <Input data-testid="emp-reg-lastname" required className="rounded-xl mt-1" value={form.lastname} onChange={up("lastname")}/>
                </div>
                <div className="sm:col-span-2">
                  <Label>Codice Fiscale *</Label>
                  <Input data-testid="emp-reg-cf" required maxLength={16} className="rounded-xl mt-1 uppercase font-mono" value={form.fiscal_code} onChange={up("fiscal_code")}/>
                </div>
                <div className="sm:col-span-2">
                  <Label>Telefono</Label>
                  <Input data-testid="emp-reg-phone" className="rounded-xl mt-1" value={form.phone} onChange={up("phone")}/>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-[#1F2540]">
              <div className="text-xs uppercase tracking-widest text-[#3B82F6] font-bold mb-3 flex items-center gap-2"><Building2 className="w-3.5 h-3.5"/> La tua azienda</div>
              <div className="space-y-3">
                <div>
                  <Label>Ragione sociale azienda *</Label>
                  <Input data-testid="emp-reg-company" required className="rounded-xl mt-1" value={form.company_name} onChange={up("company_name")} placeholder="Es. Sistemi di telecomunicazione Srl"/>
                </div>
                <div>
                  <Label>Partita IVA azienda *</Label>
                  <Input data-testid="emp-reg-vat" required className="rounded-xl mt-1 font-mono uppercase" value={form.vat_number} onChange={up("vat_number")} placeholder="11 cifre"/>
                  <div className="text-[10px] text-[#757575] mt-1">Usata per individuare l'azienda corretta che dovrà approvare il tuo accesso.</div>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-[#1F2540]">
              <div className="text-xs uppercase tracking-widest text-[#3B82F6] font-bold mb-3 flex items-center gap-2"><KeyRound className="w-3.5 h-3.5"/> Account</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <Label>Email *</Label>
                  <Input data-testid="emp-reg-email" type="email" required className="rounded-xl mt-1" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value.toLowerCase()})}/>
                </div>
                <div>
                  <Label>Password *</Label>
                  <Input data-testid="emp-reg-pwd" type="password" required minLength={6} className="rounded-xl mt-1" value={form.password} onChange={up("password")}/>
                </div>
                <div>
                  <Label>Conferma password *</Label>
                  <Input data-testid="emp-reg-pwd2" type="password" required minLength={6} className="rounded-xl mt-1" value={form.password2} onChange={up("password2")}/>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-[#1F2540]">
              <div className="text-xs uppercase tracking-widest text-[#3B82F6] font-bold mb-3">Consensi obbligatori (GDPR)</div>
              <div className="space-y-2 text-sm">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="consent-privacy"
                    checked={consents.privacy}
                    onChange={(e)=>setConsents({...consents, privacy: e.target.checked})}
                    className="mt-1 w-4 h-4 accent-[#3B82F6]"
                  />
                  <span>Ho letto e accetto la <Link to="/privacy" target="_blank" className="text-[#3B82F6] font-semibold underline">Privacy Policy</Link> (informativa ex art. 13 GDPR) *</span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="consent-tos"
                    checked={consents.tos}
                    onChange={(e)=>setConsents({...consents, tos: e.target.checked})}
                    className="mt-1 w-4 h-4 accent-[#3B82F6]"
                  />
                  <span>Accetto i <Link to="/terms" target="_blank" className="text-[#3B82F6] font-semibold underline">Termini di Servizio</Link> *</span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="consent-share"
                    checked={consents.share_data}
                    onChange={(e)=>setConsents({...consents, share_data: e.target.checked})}
                    className="mt-1 w-4 h-4 accent-[#3B82F6]"
                  />
                  <span>Acconsento alla <strong>condivisione dei miei dati con l'azienda indicata</strong> al fine di valutare la mia iscrizione (art. 6.1.a GDPR) *</span>
                </label>
              </div>
              <div className="text-[10px] text-[#757575] mt-2 italic">
                Diritto di revoca: puoi ritirare la richiesta in qualsiasi momento dal link che riceverai via email.
                Conservazione: max 30 giorni se rifiutata.
              </div>
            </div>

            <div className="pt-2">
              <Button type="submit" disabled={loading} data-testid="emp-reg-submit" className="w-full rounded-full h-12 bg-[#3B82F6] hover:bg-black text-white font-bold btn-glow">
                {loading ? "Invio…" : "Invia richiesta di iscrizione"}
              </Button>
              <div className="text-center text-xs text-[#757575] mt-3">
                Hai già un account? <Link to="/login" className="text-[#3B82F6] font-semibold hover:underline">Accedi</Link>
              </div>
              <div className="text-center text-xs text-[#757575] mt-1">
                Sei un'azienda? <Link to="/register" className="text-[#3B82F6] font-semibold hover:underline">Registra la tua azienda</Link>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
